import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Card,
  Typography,
  IconButton,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  LocalizationProvider,
  DateCalendar,
  PickersDay,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosRoundedIcon from "@mui/icons-material/ArrowBackIosRounded";
import VideocamRoundedIcon from "@mui/icons-material/VideocamRounded";
import dayjs from "dayjs";
import TodayRoundedIcon from "@mui/icons-material/TodayRounded";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import {
  deletePersonalEvents,
  getPersonalEvents,
} from "../../../Redux/Reducers/calendarReducer";
import SchedulePersonalEvent from "./SideFeedComponents/SchedulePersonalEvent";
import { useUserDetailContext } from "../../../Components/UserDetailContext";

const DeleteConfirmationDialog = ({ open, onClose, onConfirm }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">{"Delete Event"}</DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          Are you sure you want to delete this event? This action cannot be
          undone.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button onClick={onConfirm} color="error" variant="contained" autoFocus>
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ExpandableText = ({ text, maxLines = 3 }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [needsTruncation, setNeedsTruncation] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    if (textRef.current) {
      const lineHeight = parseInt(getComputedStyle(textRef.current).lineHeight);
      const maxHeight = lineHeight * maxLines;
      setNeedsTruncation(textRef.current.scrollHeight > maxHeight);
    }
  }, [text, maxLines]);

  if (!text) return null;

  return (
    <Box>
      <Typography
        ref={textRef}
        variant="body1"
        sx={{
          whiteSpace: "pre-wrap",
          lineHeight: 1.5,
          overflow: "hidden",
          display: "-webkit-box",
          WebkitLineClamp: isExpanded ? "none" : maxLines,
          WebkitBoxOrient: "vertical",
          lineClamp: isExpanded ? "none" : maxLines,
        }}
      >
        {text}
      </Typography>
      {needsTruncation && (
        <Button
          size="small"
          onClick={() => setIsExpanded(!isExpanded)}
          sx={{
            mt: 1,
            textTransform: "none",
            minWidth: "auto",
            padding: "2px 8px",
            fontSize: "0.75rem",
          }}
          color="primary"
        >
          {isExpanded ? "Show Less" : "Show More"}
        </Button>
      )}
    </Box>
  );
};

const PersonalCalender = ({ initialView = "month" }) => {
  const dispatch = useDispatch();
  const { userDetails } = useUserDetailContext();;
  const UserEvent = useSelector((state) => state?.calender?.personalEventsData) || [];
  const ProfessionalData = useSelector(
    (state) => state?.ProfileArray?.professionaProfile
  );

  const [date, setDate] = useState(dayjs());
  const [selectedDateEvents, setSelectedDateEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [dayDrawerOpen, setDayDrawerOpen] = useState(false);
  const [scheduleDrawerOpen, setScheduleDrawerOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [eventDrawerOpen, setEventDrawerOpen] = useState(false);
  const [activeModal, setActiveModal] = useState(null);

  useEffect(() => {
    dispatch(getPersonalEvents());
  }, [dispatch]);

  // Check if current user is the creator of the event
  const isEventCreator = (event) => {
    return event?.createdBy === userDetails?.email;
  };

  // Check if a date is in the past (before today)
  const isPastDate = (dateToCheck) => {
    const today = dayjs().startOf("day");
    return dateToCheck.isBefore(today, "day");
  };

  const handleDateClick = (newValue) => {
    setDate(newValue);
    const selectedDayStr = newValue.format("YYYY-MM-DD");

    const dayEvents = UserEvent?.filter(
      (event) => dayjs(event.startTime).format("YYYY-MM-DD") === selectedDayStr
    );

    setSelectedDateEvents(dayEvents);
    setActiveModal("day");
    setEventDrawerOpen(false);
    setDayDrawerOpen(true);
  };

  const handleAddNewEvent = useCallback(() => {
    setEditMode(false);
    setSelectedEvent(null);
    setActiveModal("schedule");
    setDayDrawerOpen(false);
    setEventDrawerOpen(false);
    setScheduleDrawerOpen(true);
  }, []);

  const handleEditEvent = (event) => {
    setEditMode(true);
    setSelectedEvent(event);
    setActiveModal("schedule");
    setEventDrawerOpen(false);
    setDayDrawerOpen(false);
    setTimeout(() => {
      setScheduleDrawerOpen(true);
    }, 10);
  };

  const handleDeleteEvent = (eventId) => {
    setSelectedEventId(eventId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await dispatch(deletePersonalEvents(selectedEventId));
      setDeleteDialogOpen(false);
      setDayDrawerOpen(false);
      setSelectedDateEvents([]);
      setEventDrawerOpen(false);
      dispatch(getPersonalEvents());
    } catch (error) {
      console.error("Failed to delete event:", error);
    }
  };

  const getEventColor = (eventType) => {
    switch (eventType) {
      case "Interview":
        return "#e3f2fd";
      case "Personal":
        return "#f3e5f5"; // Purple for personal events
      case "Others":
        return "#e0f7fa";
      default:
        return "#f5f5f5";
    }
  };

  // Function to handle location click - opens Google Maps
  const handleLocationClick = (location) => {
    if (location) {
      const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        location
      )}`;
      window.open(mapsUrl, "_blank");
    }
  };

  // Function to handle meet link click - opens in new tab
  const handleMeetLinkClick = (meetLink) => {
    if (meetLink) {
      window.open(meetLink, "_blank");
    }
  };

  // Function to handle event click from DayViewTimeGrid
  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setEventDrawerOpen(true);
  };

  // Function to close event drawer
  const handleCloseEventDrawer = () => {
    setEventDrawerOpen(false);
    setSelectedEvent(null);
  };

  const isPastDateTime = (dateTimeToCheck) => {
    const now = dayjs();
    return dateTimeToCheck.isBefore(now);

  }

  const DayViewTimeGrid = ({ events, date }) => {
    const getTimeSlots = () => {
      const slots = [];
      for (let i = 0; i < 24; i++) {
        slots.push(dayjs().hour(i).minute(0));
      }
      return slots;
    };

    const timeSlots = getTimeSlots();

    return (
      <Box sx={{ height: "100%", overflow: "auto", backgroundColor: "#fff" }}>
        {/* Today header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
            borderBottom: "1px solid #eee",
          }}
        >
          <Button variant="outlined" startIcon={<TodayRoundedIcon />}>
            {date.format("MMMM D, YYYY")}
          </Button>
          {/* Show add button only for today and future dates */}
          {!isPastDate(date) && (
            <IconButton
              onClick={() => {
                handleCloseEventDrawer();
                handleAddNewEvent();
              }}
            >
              <AddCircleOutlineRoundedIcon color="primary" />
            </IconButton>
          )}
        </Box>

        {/* Time slots grid */}
        {timeSlots?.map((slot) => {
          const slotEvents =
            events?.filter(
              (event) => dayjs(event?.startTime).hour() === slot.hour()
            ) || [];

          return (
            <Box
              key={slot.format("HH:mm")}
              sx={{
                display: "flex",
                borderBottom: "1px solid #eee",
                minHeight: "60px",
                position: "relative",
              }}
            >
              <Box
                sx={{
                  width: "60px",
                  p: 1,
                  color: "text.secondary",
                  fontSize: "12px",
                  textAlign: "right",
                }}
              >
                {slot.format("h A")}
              </Box>
              <Box
                sx={{
                  flex: 1,
                  position: "relative",
                  borderLeft: "1px solid #eee",
                }}
              >
                {slotEvents?.map((event, index) => {
                  const startTime = dayjs(event.startTime);
                  const endTime = dayjs(event.endTime);

                  // Calculate position based on exact minutes
                  const startMinutes =
                    startTime.hour() * 60 + startTime.minute();
                  const endMinutes = endTime.hour() * 60 + endTime.minute();
                  const duration = endMinutes - startMinutes;

                  // Calculate position as percentage of day (1440 minutes)
                  const topPosition = (startMinutes / 1440) * 100;
                  const height = (duration / 1440) * 100;

                  return (
                    <Card
                      key={`${event.id}-${index}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEventClick(event);
                      }}
                      sx={{
                        position: "absolute",
                        left: "2px",
                        right: "2px",
                        top: `${topPosition}%`,
                        height: `${height}%`,
                        minHeight: "48px",
                        p: 0.5,
                        backgroundColor: getEventColor(event.eventType),
                        cursor: "pointer",
                        "&:hover": {
                          boxShadow: 2,
                          zIndex: 2,
                          transform: "scale(1.02)",
                          transition: "all 0.2s ease",
                        },
                        zIndex: 1,
                        overflow: "hidden",
                        border:
                          event.eventType === "Personal"
                            ? "1px solid #ce93d8"
                            : event.eventType === "Interview"
                              ? "1px solid #90caf9"
                              : "1px solid #80deea",
                      }}
                    >
                      <Box sx={{ width: "100%", height: "100%" }}>
                        <Typography
                          variant="caption"
                          display="block"
                          noWrap
                          sx={{ fontSize: "10px", fontWeight: "bold" }}
                        >
                          {event.title}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: "8px" }}
                          >
                            {startTime.format("h:mm A")} -{" "}
                            {endTime.format("h:mm A")}
                          </Typography>
                          <Typography
                            color="text.secondary"
                            sx={{ fontSize: "8px" }}
                            noWrap
                          >
                            {event?.eventType}
                          </Typography>
                        </Box>
                      </Box>
                    </Card>
                  );
                })}
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  const handleCloseDayDrawer = () => {
    setDayDrawerOpen(false);
    setSelectedDateEvents([]);
    setEventDrawerOpen(false);
    dispatch(getPersonalEvents());
  };

  const getUniqueEvents = (events) => {
    const map = new Map();
    events.forEach((event) => {
      map.set(event.id, event);
    });
    return Array.from(map.values());
  };

  const todaysEvents = getUniqueEvents(
    UserEvent?.filter(
      (event) =>
        dayjs(event?.startTime).format("YYYY-MM-DD") ===
        dayjs().format("YYYY-MM-DD")
    )
  );

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        width: "25vw",
        maxWidth: "400px",
        margin: "0 auto",
      }}
    >
      {/* Monthly View */}
      <Card
        sx={{
          p: 2,
          overflow: "hidden",
          width: "100%",
          margin: "auto",
          flexShrink: 0,
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateCalendar
            value={date}
            onChange={handleDateClick}
            onMonthChange={(newValue) => {
              setDate(newValue);
            }}
            sx={{
              width: "100%",
              "& .MuiDateCalendar-root": {
                width: "100%",
              },
              "& .MuiPickersCalendarHeader-root": {
                paddingLeft: "8px",
                paddingRight: "8px",
              },
              "& .MuiDayCalendar-header": {
                width: "100%",
              },
              "& .MuiDayCalendar-weekContainer": {
                width: "100%",
              },
              "& .MuiPickersDay-day": {
                width: "32px",
                height: "32px",
                margin: "2px",
              },
            }}
            slots={{
              day: (props) => {
                const dayStr = props?.day?.format("YYYY-MM-DD");
                const dayEvents = getUniqueEvents(
                  UserEvent?.filter(
                    (event) =>
                      dayjs(event?.startTime).format("YYYY-MM-DD") === dayStr
                  )
                );

                return (
                  <PickersDay
                    {...props}
                    sx={{
                      width: "32px",
                      height: "32px",
                      fontSize: "0.875rem",
                      ...(dayEvents?.length > 0 && {
                        backgroundColor: getEventColor(dayEvents[0]?.eventType),
                        color: "#1976d2",
                        "&:hover": {
                          backgroundColor:
                            dayEvents[0]?.eventType === "Personal"
                              ? "#e1bee7"
                              : dayEvents[0]?.eventType === "Interview"
                                ? "#bbdefb"
                                : "#b2ebf2",
                        },
                      }),
                    }}
                  />
                );
              },
            }}
          />
        </LocalizationProvider>
      </Card>

      {/* Today's Events Section */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          px: 2,
          maxHeight: "50vh",
          width: "100%",
        }}
      >
        <Typography variant="h6" sx={{ p: 1 }}>
          Today's Events
        </Typography>

        {todaysEvents?.length === 0 ? (
          <Typography variant="body1" color="text.secondary" align="center">
            No events scheduled for today
          </Typography>
        ) : (
          todaysEvents
            ?.sort((a, b) => dayjs(a?.startTime).diff(dayjs(b?.startTime)))
            ?.map((event) => (
              <Accordion
                key={event?.id}
                sx={{
                  mb: 1,
                  "&:before": { display: "none" },
                  backgroundColor: getEventColor(event?.eventType),
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor:
                      event?.eventType === "Personal"
                        ? "#f3e5f5"
                        : event?.eventType === "Interview"
                          ? "#bbdefb"
                          : "#b2ebf2",
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreRoundedIcon />}
                  sx={{
                    "& .MuiAccordionSummary-content": {
                      margin: "8px 0",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                        {event?.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {dayjs(event?.startTime).format("h:mm A")} -{" "}
                        {dayjs(event?.endTime).format("h:mm A")}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      {/* Show location icon for in-person events */}
                      {event?.location && event?.location !== "Remote" && (
                        <LocationOnRoundedIcon
                          fontSize="small"
                          color="primary"
                        />
                      )}
                      {/* Show video icon for remote */}
                      {event?.location === "Remote" && event?.meetLink && (
                        <VideocamRoundedIcon fontSize="small" color="primary" />
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {event?.eventType}
                      </Typography>
                    </Box>
                  </Box>
                </AccordionSummary>

                <AccordionDetails>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    {event?.description && (
                      <ExpandableText
                        text={event?.description}
                        maxLength={100}
                      />
                    )}

                    {/* Location - In-person */}
                    {event?.location && event?.location !== "Remote" && (
                      <Button
                        variant="text"
                        onClick={() => handleLocationClick(event?.location)}
                        startIcon={<LocationOnRoundedIcon />}
                        sx={{
                          alignSelf: "flex-start",
                          textTransform: "none",
                        }}
                      >
                        {event?.location}
                      </Button>
                    )}

                    {/* Remote Meet Link */}
                    {event?.location === "Remote" && event?.meetLink && (
                      <Button
                        variant="text"
                        onClick={() => handleMeetLinkClick(event?.meetLink)}
                        startIcon={<VideocamRoundedIcon />}
                        sx={{ alignSelf: "flex-start", textTransform: "none" }}
                      >
                        Join Meeting
                      </Button>
                    )}

                    {event?.attendees?.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Attendees:
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                          {event?.attendees?.map((attendee, index) => (
                            <Chip
                              key={index}
                              avatar={
                                <Avatar
                                  src={
                                    attendee?.profile_pic ||
                                    "https://www.shutterstock.com/image-vector/default-avatar-profile-icon-transparent-600nw-2463868853.jpg"
                                  }
                                  alt={attendee?.name}
                                />
                              }
                              label={attendee?.name}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Edit/Delete buttons for creator */}
                    {isEventCreator(event) && (
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          justifyContent: "flex-end",
                          mt: 1,
                        }}
                      >
                        <Button
                          size="small"
                          startIcon={<EditRoundedIcon />}
                          onClick={() => handleEditEvent(event)}
                          color="primary"
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          startIcon={<DeleteRoundedIcon />}
                          onClick={() => handleDeleteEvent(event.id)}
                          color="error"
                        >
                          Delete
                        </Button>
                      </Box>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))
        )}
      </Box>

      {/* Day View Drawer */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          right: dayDrawerOpen ? 0 : "-25vw",
          width: "25vw",
          height: "100%",
          backgroundColor: "#fff",
          boxShadow: "-2px 0 10px rgba(0,0,0,0.1)",
          transition: "right 0.3s ease",
          zIndex: 1900,
        }}
      >
        <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
          <Box sx={{ p: 2, borderBottom: "1px solid #eee" }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <IconButton onClick={handleCloseDayDrawer}>
                <ArrowBackIosRoundedIcon />
              </IconButton>
              <Typography variant="h6">
                {date.format("MMMM D, YYYY")}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ flex: 1, overflow: "auto" }}>
            <DayViewTimeGrid events={selectedDateEvents} date={date} />
          </Box>
        </Box>
      </Box>

      {/* Event Details Drawer */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          right: eventDrawerOpen ? 0 : "-400px",
          width: "400px",
          height: "100%",
          backgroundColor: "#fff",
          boxShadow: "-2px 0 10px rgba(0,0,0,0.1)",
          transition: "right 0.3s ease",
          zIndex: 2100,
          overflow: "auto",
        }}
      >
        <Box sx={{ p: 3, height: "100%", overflow: "auto" }}>
          {selectedEvent && (
            <>
              {/* Header */}
              <Box
                sx={{
                  mb: 3,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  pb: 2,
                  borderBottom: "1px solid #eee",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: "#1a1a1a",
                      textTransform: "capitalize"
                    }}
                  >
                    Personal Event
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                  {isEventCreator(selectedEvent) && (
                    <>
                      <IconButton
                        onClick={() => handleEditEvent(selectedEvent)}
                        color="primary"
                        size="small"
                      >
                        <EditRoundedIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeleteEvent(selectedEvent?.id)}
                        color="primary"
                        size="small"
                      >
                        <DeleteRoundedIcon />
                      </IconButton>
                    </>
                  )}
                  <IconButton onClick={handleCloseEventDrawer}>
                    <CloseIcon />
                  </IconButton>
                </Box>
              </Box>

              {/* Title and Event Type */}
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  backgroundColor: "#f8f9fa",
                  borderRadius: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Title
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    {selectedEvent?.title}
                  </Typography>
                </Box>
              </Box>

              {/* Description */}
              {selectedEvent?.description && (
                <Box sx={{ mb: 3, backgroundColor: "#f8f9fa", p: 2, borderRadius: 2 }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Description
                  </Typography>
                  <ExpandableText
                    text={selectedEvent?.description}
                    maxLength={20}
                  />
                </Box>
              )}


              {/* Time & Duration */}
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  backgroundColor: "#f8f9fa",
                  borderRadius: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Box>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Date & Time
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {dayjs(selectedEvent?.startTime).format(
                        "DD MMM YYYY, hh:mm A"
                      )}{" "}
                      - {dayjs(selectedEvent?.endTime).format("hh:mm A")}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                </Box>
              </Box>

              {/* Location */}
              {selectedEvent?.location && selectedEvent?.location !== "Remote" && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Location
                  </Typography>
                  <Paper sx={{ p: 2, backgroundColor: "#f8f9fa" }}>
                    <Button
                      variant="text"
                      onClick={() => handleLocationClick(selectedEvent?.location)}
                      startIcon={<LocationOnRoundedIcon />}
                      sx={{ textTransform: "none" }}
                    >
                      {selectedEvent?.location}
                    </Button>
                  </Paper>
                </Box>
              )}

              {/* Remote Meet Link */}
              {selectedEvent?.location === "Remote" && selectedEvent?.meetLink && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Meeting Link
                  </Typography>
                  <Paper sx={{ p: 2, backgroundColor: "#f8f9fa" }}>
                    <Button
                      variant="text"
                      onClick={() => handleMeetLinkClick(selectedEvent?.meetLink)}
                      startIcon={<VideocamRoundedIcon />}
                      sx={{ textTransform: "none" }}
                    >
                      Join Virtual Meeting
                    </Button>
                  </Paper>
                </Box>
              )}


              {/* Attendees */}
              {selectedEvent?.attendees && selectedEvent?.attendees?.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Attendees
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {selectedEvent?.attendees?.map((attendee, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          p: 1,
                          backgroundColor: "#f8f9fa",
                          borderRadius: 2,
                        }}
                      >
                        <Avatar
                          src={
                            attendee?.profile_pic ||
                            "https://www.shutterstock.com/image-vector/default-avatar-profile-icon-transparent-600nw-2463868853.jpg"
                          }
                          sx={{ width: 32, height: 32, mr: 1 }}
                        />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {attendee?.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {attendee?.email}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Created By Information */}
              {selectedEvent?.createdBy && (
                <Box sx={{ mt: 3, pt: 2, borderTop: "1px solid #eee" }}>
                  <Typography variant="body2" color="text.secondary">
                    Created by: {selectedEvent?.createdBy}
                  </Typography>
                  {isEventCreator(selectedEvent) && (
                    <Typography variant="caption" color="primary">
                      (You created this event)
                    </Typography>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Backdrop for event drawer */}
      {eventDrawerOpen && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 2099,
          }}
          onClick={handleCloseEventDrawer}
        />
      )}

      {/* Backdrop for day drawer */}
      {dayDrawerOpen && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 1899,
          }}
          onClick={handleCloseDayDrawer}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
      />

      {/* Schedule Personal Event Drawer */}
      <SchedulePersonalEvent
        open={scheduleDrawerOpen}
        onClose={() => {
          setScheduleDrawerOpen(false);
          setEditMode(false);
          setSelectedEvent(null);
          dispatch(getPersonalEvents());
        }}
        selectedEvent={editMode ? selectedEvent : null}
        selectedDate={date}
        isPastDate={isPastDateTime}
      />
    </Box>
  );
};

export default PersonalCalender;